import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  Clock,
  Facebook,
  Loader2,
  Send,
  Trash2,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { Label } from '@orisini/ui/components/label'
import { Textarea } from '@orisini/ui/components/textarea'
import { ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type AccountDto } from '@/features/accounts/api'
import MediaPicker from '../components/MediaPicker'
import SlideList, { MAX_SLIDES, type SlideDraft } from '../components/SlideList'
import { postsApi, type PacingHoldDto, type PostDto } from '../api'

/**
 * Komposer post Instagram.
 *
 * Satu halaman untuk Post (tunggal & carousel) dan Reel — bukan dua komposer
 * terpisah, karena keduanya berbagi hampir seluruh field dan yang berbeda
 * cuma jumlah media dan segelintir opsi.
 *
 * Story sengaja tidak ada: UI web Instagram sering tidak menyediakannya, dan
 * jalur yang bisa menjangkaunya menuntut sidik jari mobile di atas sesi
 * desktop — ketidakcocokan yang justru dicari sistem integritas.
 */
const CAPTION_MAKS = 2_200

export default function PostEditorPage() {
  const { id } = useParams<{ id: string }>()
  const baru = id === 'baru'
  const navigate = useNavigate()

  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [accountId, setAccountId] = useState('')
  const [format, setFormat] = useState<'post' | 'reel'>('post')
  const [slides, setSlides] = useState<SlideDraft[]>([])
  const [caption, setCaption] = useState('')
  const [firstComment, setFirstComment] = useState('')
  const [crossPostFb, setCrossPostFb] = useState(false)
  const [hideLike, setHideLike] = useState(false)
  const [disableComments, setDisableComments] = useState(false)
  const [post, setPost] = useState<PostDto | null>(null)
  const [hold, setHold] = useState<PacingHoldDto | null>(null)
  const [memuat, setMemuat] = useState(!baru)
  const [sibuk, setSibuk] = useState(false)
  /** Nilai `<input type="datetime-local">` — null berarti pemilih waktu tertutup. */
  const [waktuJadwal, setWaktuJadwal] = useState<string | null>(null)

  useEffect(() => {
    void accountsApi.list().then((r) => {
      setAccounts(r.accounts)
      if (r.accounts.length === 1) setAccountId((a) => a || r.accounts[0].id)
    })
  }, [])

  useEffect(() => {
    if (baru || !id) return
    void postsApi
      .get(id)
      .then((r) => {
        const p = r.post
        setPost(p)
        setHold(r.hold)
        setAccountId(p.accountId)
        setFormat(p.format)
        setSlides(
          p.slides.map((s) => ({
            mediaId: s.mediaId,
            altText: s.altText ?? '',
            media: s.media,
          })),
        )
        setCaption(p.caption ?? '')
        setFirstComment(p.firstComment ?? '')
        setCrossPostFb(p.crossPostToFacebook)
        setHideLike(p.hideLikeCount)
        setDisableComments(p.disableComments)
      })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setMemuat(false))
  }, [id, baru])

  const terkunci = post?.status === 'dispatched' || post?.status === 'posting'
  const sudahTerbit = post?.status === 'posted'
  const akun = accounts.find((a) => a.id === accountId)
  // Reel = satu video (Meta: `media_type=REELS` hanya menerima satu `video_url`).
  // Klip-klip yang digabung di editor Instagram tetap terbit sebagai satu berkas.
  const batasMedia = format === 'reel' ? 1 : MAX_SLIDES

  const masalah = useMemo(() => {
    const out: string[] = []
    if (!accountId) out.push('A target Instagram account is required')
    if (!slides.length) out.push('At least one media file is required')
    if (slides.length > MAX_SLIDES) out.push(`Maximum ${MAX_SLIDES} slides`)
    if (format === 'reel' && slides.length > 1) out.push('A reel can only have one video')
    if (format === 'reel' && slides[0] && slides[0].media.kind !== 'video') {
      out.push('A reel must be a video')
    }
    return out
  }, [accountId, slides, format])

  function badan() {
    return {
      accountId,
      format,
      slides: slides.map((s) => ({ mediaId: s.mediaId, altText: s.altText || null })),
      caption,
      firstComment,
      crossPostToFacebook: crossPostFb,
      hideLikeCount: hideLike,
      disableComments,
    }
  }

  async function simpan(): Promise<PostDto | null> {
    setSibuk(true)
    try {
      const r = baru ? await postsApi.create(badan()) : await postsApi.update(id!, badan())
      setPost(r.post)
      if (baru) navigate(`/posts/${r.post.id}`, { replace: true })
      return r.post
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : String(e))
      return null
    } finally {
      setSibuk(false)
    }
  }

  async function simpanLaluAntre() {
    const p = await simpan()
    if (!p) return
    setSibuk(true)
    try {
      const r = await postsApi.enqueue(p.id)
      setPost(r.post)
      toast.success(
        r.post.scheduledAt
          ? `Added to queue — publishing ${new Date(r.post.scheduledAt).toLocaleString('en-GB')}`
          : 'Added to queue',
      )
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSibuk(false)
    }
  }

  /** Simpan, lalu terbitkan sekarang — atau secepat pacing akun mengizinkan. */
  async function simpanLaluPostNow() {
    const p = await simpan()
    if (!p) return
    setSibuk(true)
    try {
      const r = await postsApi.postNow(p.id)
      setPost(r.post)
      setHold(r.hold)
      if (r.hold) {
        toast.message(`Held by account pacing — publishing at ${jam(r.hold.freeAt)} at the earliest`)
      } else {
        toast.success('Sent to the agent — publishing now')
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSibuk(false)
    }
  }

  /** Simpan, lalu paku ke waktu pilihan — di luar slot antrean. */
  async function simpanLaluJadwalkan() {
    if (!waktuJadwal) return
    // `datetime-local` tidak membawa zona: diartikan sebagai waktu lokal
    // browser, yang memang zona yang dilihat user saat memilihnya.
    const at = new Date(waktuJadwal).getTime()
    if (!Number.isFinite(at)) {
      toast.error('Pick a date and time')
      return
    }
    const p = await simpan()
    if (!p) return
    setSibuk(true)
    try {
      const r = await postsApi.postTo(p.id, at)
      setPost(r.post)
      setHold(r.hold)
      setWaktuJadwal(null)
      toast.success(
        r.hold
          ? `Scheduled — account pacing moves it to ${jam(r.hold.freeAt)} at the earliest`
          : `Scheduled for ${jam(at)}`,
      )
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSibuk(false)
    }
  }

  async function hapus() {
    if (!id || baru) return
    setSibuk(true)
    try {
      await postsApi.remove(id)
      toast.success('Post deleted')
      navigate('/posts')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSibuk(false)
    }
  }

  if (memuat) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/posts')}>
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {baru ? 'New post' : 'Edit post'}
        </h1>
        {post && (
          <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
            {post.status}
          </span>
        )}
      </div>

      {terkunci && (
        <p className="flex items-start gap-2 rounded-md bg-amber-500/10 p-3 text-sm text-amber-500">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          The agent is working on this post. Its contents cannot be changed until it finishes — the
          agent already holds a copy of the data.
        </p>
      )}

      {post?.status === 'scheduled' && post.scheduledAt && (
        <div className="space-y-1 rounded-md bg-muted/50 p-3 text-sm">
          <p className="flex items-center gap-2">
            <Clock className="size-4 shrink-0 text-muted-foreground" />
            Scheduled for {jam(post.scheduledAt)}
          </p>
          {hold && (
            <p className="pl-6 text-amber-500">
              Held by account pacing until {jam(hold.freeAt)} —{' '}
              {hold.reason === 'min_gap'
                ? `this account waits at least ${akun?.minGapMinutes ?? '…'} minutes between posts.`
                : `this account publishes at most ${akun?.maxPostsPerHour ?? '…'} posts per hour.`}{' '}
              It will go out by itself; there is nothing to do.
            </p>
          )}
        </div>
      )}

      {sudahTerbit && (
        <div className="space-y-2 rounded-md bg-emerald-500/10 p-3 text-sm text-emerald-500">
          <p>
            Published
            {post?.igPermalink ? (
              <>
                {' — '}
                <a href={post.igPermalink} target="_blank" rel="noreferrer" className="underline">
                  open in Instagram
                </a>
              </>
            ) : (
              // Post yang terbit TANPA permalink adalah keadaan tersendiri, bukan
              // kegagalan: mengulanginya berarti dua post.
              ' — but the link was not captured, so it is unverified'
            )}
          </p>
          {post?.fieldsSkipped.length ? (
            <p className="text-xs">
              Published without: {post.fieldsSkipped.join(', ')}
            </p>
          ) : null}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Account & format</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="akun">Instagram account</Label>
            <select
              id="akun"
              value={accountId}
              disabled={terkunci || !baru}
              onChange={(e) => setAccountId(e.target.value)}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60"
            >
              <option value="">— select account —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username ? `@${a.username}` : a.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label>Format</Label>
            <div className="flex gap-2">
              {(['post', 'reel'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  disabled={terkunci}
                  onClick={() => setFormat(f)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-sm capitalize',
                    format === f ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground',
                  )}
                >
                  {f === 'post' ? 'Post / carousel' : 'Reel'}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {format === 'reel' ? 'Video' : 'Media'}{' '}
            {slides.length > 0 && `(${slides.length}/${batasMedia})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <SlideList slides={slides} onChange={setSlides} format={format} />

          {!terkunci && slides.length < batasMedia && (
            <MediaPicker
              videoOnly={format === 'reel'}
              onPick={(m) => {
                // Tab URL & file lokal tidak bisa dibatasi lewat `accept`,
                // jadi jenisnya diperiksa lagi di sini.
                if (format === 'reel' && m.kind !== 'video') {
                  toast.error('A reel must be a video')
                  return
                }
                // Media yang sama tidak boleh jadi dua slide: `mediaId` dipakai
                // sebagai id drag-and-drop, dan id ganda membuat pengurutannya
                // kacau tanpa pesan kesalahan apa pun.
                if (slides.some((s) => s.mediaId === m.id)) {
                  toast.error('That media is already one of the slides')
                  return
                }
                setSlides((s) => [...s, { mediaId: m.id, altText: '', media: m }])
              }}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Caption</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Textarea
              value={caption}
              disabled={terkunci}
              maxLength={CAPTION_MAKS}
              rows={5}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a caption…"
            />
            <p className="text-right text-xs text-muted-foreground">
              {caption.length}/{CAPTION_MAKS}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="komentar1">First comment</Label>
            <Textarea
              id="komentar1"
              value={firstComment}
              disabled={terkunci}
              rows={2}
              onChange={(e) => setFirstComment(e.target.value)}
              placeholder="Sent as a comment after the post is published — the usual place for hashtags."
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Opsi
            label="Hide like count"
            checked={hideLike}
            disabled={terkunci}
            onChange={setHideLike}
          />
          <Opsi
            label="Turn off comments"
            checked={disableComments}
            disabled={terkunci}
            onChange={setDisableComments}
          />
          <Opsi
            label="Also share to Facebook"
            checked={crossPostFb}
            disabled={terkunci}
            onChange={setCrossPostFb}
            icon={<Facebook className="size-3.5" />}
            catatan={
              // Ini bukan sekadar kenyamanan: setelan Instagram-nya LENGKET per
              // akun, jadi kalau tidak dipaksa mati, post berikutnya ikut
              // tersebar ke Page tanpa diminta.
              'Enforced to match this choice on every post — Instagram remembers the last setting per account.'
            }
          />
        </CardContent>
      </Card>

      {masalah.length > 0 && (
        <ul className="space-y-1 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
          {masalah.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => void simpan()} disabled={sibuk || terkunci || sudahTerbit}>
          {sibuk && <Loader2 className="size-4 animate-spin" />}
          Save draft
        </Button>
        <Button
          variant="default"
          onClick={() => void simpanLaluAntre()}
          disabled={sibuk || terkunci || sudahTerbit || masalah.length > 0}
        >
          <Send className="size-4" />
          Save & add to queue
        </Button>
        <Button
          variant="outline"
          onClick={() => void simpanLaluPostNow()}
          disabled={sibuk || terkunci || sudahTerbit || masalah.length > 0}
        >
          <Zap className="size-4" />
          Post now
        </Button>
        <Button
          variant="outline"
          onClick={() => setWaktuJadwal((w) => (w === null ? waktuAwal() : null))}
          disabled={sibuk || terkunci || sudahTerbit || masalah.length > 0}
        >
          <CalendarClock className="size-4" />
          Schedule…
        </Button>
        {!baru && (
          <Button variant="ghost" onClick={() => void hapus()} disabled={sibuk || terkunci}>
            <Trash2 className="size-4" />
            Delete
          </Button>
        )}
      </div>

      {waktuJadwal !== null && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
          <div className="space-y-1.5">
            <Label htmlFor="waktu-jadwal">Publish at</Label>
            <input
              id="waktu-jadwal"
              type="datetime-local"
              value={waktuJadwal}
              min={waktuAwal()}
              onChange={(e) => setWaktuJadwal(e.target.value)}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            />
          </div>
          <Button onClick={() => void simpanLaluJadwalkan()} disabled={sibuk || !waktuJadwal}>
            {sibuk && <Loader2 className="size-4 animate-spin" />}
            Save & schedule
          </Button>
          <Button variant="ghost" onClick={() => setWaktuJadwal(null)} disabled={sibuk}>
            Cancel
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            Your local time. Outside the queue's slots — the account's pacing rules still apply.
          </p>
        </div>
      )}
    </div>
  )
}

/** "28 Oct, 12:51" — en-GB, 24 jam, sesuai konvensi antarmuka. */
function jam(ms: number): string {
  return new Date(ms).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Nilai awal pemilih waktu: 10 menit dari sekarang, dibulatkan ke menit.
 * Server menolak jadwal kurang dari satu menit ke depan.
 */
function waktuAwal(): string {
  const d = new Date(Date.now() + 10 * 60_000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

function Opsi({
  label,
  checked,
  disabled,
  onChange,
  icon,
  catatan,
}: {
  label: string
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
  icon?: React.ReactNode
  catatan?: string
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4"
      />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm">
          {icon}
          {label}
        </span>
        {catatan && <span className="block text-xs text-muted-foreground">{catatan}</span>}
      </span>
    </label>
  )
}
